import React, { forwardRef, ReactElement, useSyncExternalStore, useState } from 'react';
import { Editor, Monaco, EditorProps } from '@monaco-editor/react';
import {
  ArrowsPointingOutIcon,
  XMarkIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import { CopyToClipboard } from '../copy-to-clipboard-v2';
import { editor } from 'monaco-editor';

import { useTranslation } from 'react-i18next';

function subscribeToTheme(callback: () => void) {
  const observer = new MutationObserver(callback);
  observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
  return () => observer.disconnect();
}
function getThemeSnapshot() {
  return document.documentElement.classList.contains('dark') ? 'vs-dark' : 'light';
}

export type CodeInputProps = {
  value?: string;
  /**
   * Optional rendered as the label for this input
   */
  label?: string | ReactElement<any>;
  errorMessage?: string | ReactElement<any>;
  /**
   * Whether this input is in error and the error message is rendered externally.
   * Set to true to render the input in error state with no internal message.
   */
  hasExternalErrors?: boolean;
  /**
   * The function that is called with the new value and the original event when the input value changes
   * The consumer of this component should use that data to update the `value` prop passed to
   * this component.
   */
  onChange?: (newValue: string) => void;
  /**
   * Array of validation schemas to be used to validate the JSON content. Used only for language=JSON
   */
  validationSchemas?: Record<string, unknown>[];
  /**
   * Language of the code. All Monaco languages are supported. Defaults to 'json'.
   */
  language?: string;
  /**
   * Make editor read-only
   */
  readOnly?: boolean;
  /**
   * Show/Hide Search button
   */
  enableSearch?: boolean;
  /**
   * Show/Hide Clipboard Copy Button
   */
  showCopyButton?: boolean;
  /**
   * Show/Hide Expand  Button
   */
  showExpandIcon?: boolean;
  /**
   * Virtual file name used in matching validation schemas
   */
  fileName?: string;
  options?: EditorProps['options'];
  onMount?: EditorProps['onMount'];
  className?: string;
} & Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'>;

function calculateClassNames({
  hasErrors,
  hasFocus,
}: {
  hasErrors: boolean;
  hasFocus: boolean;
}) {
  const baseClassNames =
    'block ' +
    'placeholder-gray-400 dark:placeholder-gray-500 ' +
    'sm:text-sm ' +
    'border-gray-300 dark:border-gray-600 ' +
    'disabled:bg-gray-100 ' +
    'rounded-md ' +
    'p-[1px] ' +
    'bg-white dark:bg-gray-900 ' +
    'shadow-sm ' +
    'w-full ' +
    'border-2 ';
  const errorClassNames = hasFocus
    ? 'ring ring-rose-200 border-rose-500 outline-none '
    : 'border-rose-600 ';
  const normalClassNames = hasFocus
    ? 'ring ring-transparent border-br-primary outline-none '
    : ' ';
  const stateClassNames = hasErrors ? errorClassNames : normalClassNames;
  return `${baseClassNames} ${stateClassNames}`;
}

const CodeInput = forwardRef(
  (
    {
      value,
      label,
      errorMessage,
      hasExternalErrors,
      onChange,
      validationSchemas,
      language = 'json',
      readOnly = false,
      enableSearch = false,
      showCopyButton = false,
      showExpandIcon = true,
      fileName,
      options,
      className = '',
      onMount,
      ...props
    }: CodeInputProps,
    ref: any,
  ) => {
    const { t } = useTranslation('designSystem');
    const [hasFocus, setHasFocus] = useState(false);
    const [isMaximised, setIsMaximised] = useState(false);
    const editorRef = React.useRef<any>(undefined);
    const monacoTheme = useSyncExternalStore(subscribeToTheme, getThemeSnapshot);
    const handleEditorDidMount = (
      editor: editor.IStandaloneCodeEditor,
      monaco: Monaco,
    ) => {
      editorRef.current = editor;
      if (ref) {
        ref.current = editor;
      }
      if (editor) {
        editor.onDidFocusEditorWidget(() => {
          setHasFocus(true);
        });
        editor.onDidBlurEditorWidget(() => {
          setHasFocus(false);
        });
        editor.updateOptions({ readOnly: readOnly });
      }

      if (language === 'json' && validationSchemas?.length) {
        const existingDiagnosticsOptions =
          monaco.languages.json.jsonDefaults.diagnosticsOptions;
        const existingSchemas = existingDiagnosticsOptions?.schemas || [];
        const newSchemas = validationSchemas
          .map((schema, index) => {
            const monacoSchema: Record<string, unknown> = {
              uri: schema.$id,
              schema,
            };
            if (index === 0) {
              monacoSchema.fileMatch = [fileName];
            }
            const existingSchema = existingSchemas.find(
              ({ uri }: { uri: string }) => uri === schema.$id,
            );
            if (existingSchema) {
              if (index === 0) {
                (existingSchema as any).fileMatch = [fileName];
              }
              return;
            }
            return monacoSchema;
          })
          .filter(Boolean);

        monaco.languages.json.jsonDefaults.setDiagnosticsOptions({
          validate: true,
          schemas: existingSchemas.concat(newSchemas as any),
        });
      }

      onMount?.(editor, monaco);
    };

    const classNames = calculateClassNames({
      hasErrors: !!errorMessage || !!hasExternalErrors,
      hasFocus,
    });

    const handleChange = (newValue: any) => {
      onChange?.(newValue);
    };

    const handleMaximise = () => {
      setIsMaximised(true);
    };
    const handleRestore = () => {
      setIsMaximised(false);
    };

    return (
      <div {...props}>
        <div
          className={`flex flex-col ${
            isMaximised
              ? 'fixed top-0 right-0 bottom-0 left-0 z-[150] bg-white dark:bg-gray-900 p-2'
              : 'relative h-full'
          }`}
        >
          <div className="flex flex-row justify-end gap-1">
            {label && (
              <label className="block shrink-0 grow text-sm font-medium text-gray-700 dark:text-gray-300">
                {label}
              </label>
            )}
            {enableSearch && (
              <span
                className="inline-block cursor-pointer px-1 opacity-60 group-hover:visible hover:opacity-100"
                title={t('openSearch')}
                onClick={() => {
                  editorRef.current.getAction('actions.find').run();
                }}
              >
                <MagnifyingGlassIcon className="block h-5 w-5" />
              </span>
            )}
            {value && showCopyButton && (
              <CopyToClipboard value={value} className="h-5 w-5" />
            )}
            {showExpandIcon && (
              <>
                {isMaximised ? (
                  <span
                    className="inline-block cursor-pointer px-1 opacity-60 group-hover:visible hover:opacity-100"
                    title={t('restoreEditor')}
                    onClick={handleRestore}
                  >
                    <XMarkIcon className="block h-5 w-5" />
                  </span>
                ) : (
                  <span
                    className="inline-block cursor-pointer px-1 opacity-60 group-hover:visible hover:opacity-100"
                    title={t('maximiseEditor')}
                    onClick={handleMaximise}
                  >
                    <ArrowsPointingOutIcon className="block h-5 w-5" />
                  </span>
                )}
              </>
            )}
          </div>

          <div className="mt-1 grow overflow-hidden">
            <Editor
              value={value}
              path={fileName}
              defaultLanguage="json"
              theme={monacoTheme}
              onChange={handleChange}
              className={`${classNames} ${className}`}
              onMount={handleEditorDidMount}
              options={options}
            />
          </div>
          {errorMessage && (
            <div className="mt-1 mb-3 max-h-20 overflow-y-auto text-sm text-red-500">
              {errorMessage}
            </div>
          )}
        </div>
      </div>
    );
  },
);

CodeInput.displayName = 'CodeInput';
export { CodeInput };
